// ====================================================== //
// Admin.js — login + News | Employees | Recs             //
// ====================================================== //
import React, { useEffect, useState, useRef } from "react";
import "./NewsPage.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API_BASE } from "../config";
import { authFetch } from "../utils/authFetch";
import ImageCropModal from "../components/ImageCropModal"; // supports mode="people" | "newsRect"

// Normalize list-shaped API responses into arrays
const toArray = (d) => {
  if (Array.isArray(d)) return d;
  if (!d || typeof d !== "object") return [];
  for (const k of [
    "items",
    "data",
    "rows",
    "results",
    "list",
    "news",
    "employees",
    "emps",
    "recs",
    "recommendations",
  ]) {
    if (Array.isArray(d[k])) return d[k];
  }
  console.warn("Unexpected list response shape:", d);
  return [];
};

/* ---------- helpers ---------- */
const todayISO = () => new Date().toISOString().slice(0, 10);

const parseMaybeJson = (v) => {
  if (!v) return null;
  if (typeof v === "string") {
    try { return JSON.parse(v); } catch { return null; }
  }
  return v;
};

/** Compute CSS for a circular avatar from image_json ('avatarCircle'). */
function getAvatarCropStyles(imageJson, presetKey = "avatarCircle", sizePx = 80) {
  const j = parseMaybeJson(imageJson);
  if (!j || typeof j !== "object") return null;

  const iw = Number(j?.original?.width);
  const ih = Number(j?.original?.height);
  if (!iw || !ih) return null;

  const c = j?.crops?.[presetKey];
  if (!c || c.shape !== "circle" || !c.center || typeof c.radius !== "number") return null;

  const cx = Number(c.center.x);
  const cy = Number(c.center.y);
  const r = Number(c.radius);
  if (!isFinite(cx) || !isFinite(cy) || !isFinite(r) || r <= 0) return null;

  const sidePx = 2 * r * Math.min(iw, ih); // square bounding the circle

  const xNorm = (cx * iw - sidePx / 2) / iw;
  const yNorm = (cy * ih - sidePx / 2) / ih;
  const wNorm = sidePx / iw;
  const hNorm = sidePx / ih;
  if (wNorm <= 0 || hNorm <= 0) return null;

  const container = {
    position: "relative",
    width: `${sizePx}px`,
    height: `${sizePx}px`,
    borderRadius: "50%",
    overflow: "hidden",
    background: "#f3f3f3",
    flex: "0 0 auto",
    boxShadow: "0 0 0 2px rgba(255,255,255,0.9)",
  };

  const img = {
    position: "absolute",
    display: "block",
    width: `${(1 / wNorm) * 100}%`,
    height: "auto",
    left: `-${(xNorm / wNorm) * 100}%`,
    top: `-${(yNorm / hNorm) * 100}%`,
    maxWidth: "none",
    userSelect: "none",
    pointerEvents: "none",
  };

  return { container, img };
}


/** Compute CSS for the article image crop from crop_json. */
function getArticleCropStyles(imageJson) {
  const j = parseMaybeJson(imageJson);
  if (!j || typeof j !== "object") return null;

  const iw = Number(j?.original?.width);
  const ih = Number(j?.original?.height);
  if (!iw || !ih) return null;

  const ac = j?.articleCrop;
  // If no crop or disabled, render full image with original aspect
  if (!ac || ac.enabled === false || !ac.box) {
    const container = {
      position: "relative",
      width: "100%",
      aspectRatio: (iw / ih),
      overflow: "hidden",
      borderRadius: 8,
      background: "#f3f3f3",
    };
    const img = {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      userSelect: "none",
    };
    return { container, img, isCropped: false };
  }

  const { x, y, width, height } = ac.box;
  if (![x, y, width, height].every(n => typeof n === "number" && isFinite(n))) {
    return null;
  }

  // container keeps the crop's aspect ratio
  const cropAspect = (iw / ih) * (width / height);
  const container = {
    position: "relative",
    width: "100%",
    aspectRatio: cropAspect,
    overflow: "hidden",
    borderRadius: 8,
    background: "#f3f3f3",
  };

  // scale image so the crop rect exactly fills the container
  const img = {
    position: "absolute",
    display: "block",
    width: `${(1 / width) * 100}%`,
    height: "auto",
    left: `-${(x / width) * 100}%`,
    top: `-${(y / height) * 100}%`,
    maxWidth: "none",
    userSelect: "none",
    pointerEvents: "none",
  };

  return { container, img, isCropped: true };
}


/* ---------- login form ------ */
const LoginForm = ({ onSuccess }) => {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    const res = await fetch(`${API_BASE}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ username: u, password: p }),
    });
    if (res.ok) onSuccess();
    else setErr("Wrong username / password");
  };

  return (
    <section className="news-card" style={{ maxWidth: 320, margin: "4rem auto" }}>
      <h3>Admin login</h3>
      <form onSubmit={submit}>
        <input
          value={u}
          onChange={(e) => setU(e.target.value)}
          placeholder="Username"
          style={{ width: "100%", marginBottom: ".5rem" }}
        />
        <input
          type="password"
          value={p}
          onChange={(e) => setP(e.target.value)}
          placeholder="Password"
          style={{ width: "100%", marginBottom: ".5rem" }}
        />
        {err && <p style={{ color: "red" }}>{err}</p>}
        <button type="submit">Log in</button>
      </form>
    </section>
  );
};

/* ---------- BlockEditor (NEWS): calls modal in newsRect mode ---------- */
const BlockEditor = ({ blocks, setBlocks, openCrop }) => {
  const addBlock = (type) => {
    const newB =
      type === "text"
        ? { type: "text", body_md: "" }
        : type === "quote"
          ? { type: "quote", body_md: "", source: "" }
          : { type: "image", path: "", crop_json: null };
    setBlocks([...blocks, newB]);
  };

  const updateBlock = (i, p) =>
    setBlocks(blocks.map((b, idx) => (idx === i ? { ...b, ...p } : b)));
  const moveBlock = (i, d) => {
    const c = [...blocks];
    const [b] = c.splice(i, 1);
    c.splice(i + d, 0, b);
    setBlocks(c);
  };
  const removeBlock = (i) => setBlocks(blocks.filter((_, idx) => idx !== i));

  // NEWS uses just a focus point
  const handleFile = async (idx, file) => {
    openCrop({ file, mode: "newsRect" }, async (cropJson) => {
      try {
        const fd = new FormData();
        fd.append("image", file);
        fd.append("crop_json", JSON.stringify(cropJson));
        const res = await authFetch(`${API_BASE}/upload-image`, { method: "POST", body: fd });

        if (!res.ok) return alert("Upload failed");
        const { path } = await res.json();
        updateBlock(idx, { path, crop_json: cropJson });
      } catch (e) {
        console.error(e);
        alert("Upload failed");
      }
    });
  };

  return (
    <div className="block-editor">
      {blocks.map((b, idx) => (
        <div key={idx} className="news-card" style={{ marginBottom: "1rem" }}>
          <header className="news-card__header">
            <strong>
              {b.type.toUpperCase()} #{idx + 1}
            </strong>
            <div>
              <button disabled={idx === 0} onClick={() => moveBlock(idx, -1)}>
                ↑
              </button>
              <button
                disabled={idx === blocks.length - 1}
                onClick={() => moveBlock(idx, 1)}
              >
                ↓
              </button>
              <button onClick={() => removeBlock(idx)}>✕</button>
            </div>
          </header>

          {b.type === "text" && (
            <textarea
              rows={3}
              style={{ width: "100%" }}
              placeholder="Markdown text…"
              value={b.body_md}
              onChange={(e) => updateBlock(idx, { body_md: e.target.value })}
            />
          )}

          {b.type === "quote" && (
            <>
              <textarea
                rows={2}
                style={{ width: "100%" }}
                placeholder="> A pull quote…"
                value={b.body_md}
                onChange={(e) => updateBlock(idx, { body_md: e.target.value })}
              />
              <input
                style={{ width: "100%", marginTop: ".5rem" }}
                placeholder="Source"
                value={b.source}
                onChange={(e) => updateBlock(idx, { source: e.target.value })}
              />
            </>
          )}

          {b.type === "image" && (
            <>
              {b.path && (
                <div style={{ maxWidth: 640, margin: "0.5rem auto" }}>
                  {(() => {
                    const styles = getArticleCropStyles(b.crop_json);
                    if (styles) {
                      return (
                        <div style={styles.container}>
                          <img src={b.path} alt="" style={styles.img} />
                        </div>
                      );
                    }
                    // Fallback (no json / no original size): still constrain to article width
                    return (
                      <img
                        src={b.path}
                        alt=""
                        style={{ width: "100%", borderRadius: 8, display: "block" }}
                      />
                    );
                  })()}
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onClick={(e) => { e.target.value = null; }}
                onChange={(e) => {
                  const f = e.target.files[0];
                  if (!f) return;
                  handleFile(idx, f);
                }}
              />
            </>
          )}

        </div>
      ))}

      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <button onClick={() => addBlock("text")}>+ Text</button>{" "}
        <button onClick={() => addBlock("quote")}>+ Quote</button>{" "}
        <button onClick={() => addBlock("image")}>+ Image</button>
      </div>
    </div>
  );
};

/* ====================================================== */
/* Admin root                                             */
/* ====================================================== */
const Admin = () => {
  /* auth gate */
  const [authed, setAuthed] = useState(null); // null=checking
  const [tab, setTab] = useState("news");

  /* data states */
  const [news, setNews] = useState([]);
  const [nLoad, setNL] = useState(true);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [published, setPub] = useState(todayISO());
  const [blocks, setBlocks] = useState([]);

  const [emps, setEmps] = useState([]);
  const [eLoad, setEL] = useState(true);
  const [eName, setEName] = useState("");
  const [eRole, setERole] = useState("");
  const [eBranch, setEBranch] = useState("");
  const [eFile, setEFile] = useState(null);
  const [eCrop, setECrop] = useState(null);

  const [recs, setRecs] = useState([]);
  const [rLoad, setRL] = useState(true);
  const [rQuote, setRQuote] = useState("");
  const [rName, setRName] = useState("");
  const [rTitle, setRTitle] = useState("");
  const [rFile, setRFile] = useState(null);
  const [rCrop, setRCrop] = useState(null);

  /* success flashes */
  const [eSavedOK, setESavedOK] = useState(false);
  const [rSavedOK, setRSavedOK] = useState(false);

  /* file input refs (to clear filename text) */
  const eFileRef = useRef(null);
  const rFileRef = useRef(null);

  /* ---------- check session on mount --------- */
  useEffect(() => {
    authFetch(`${API_BASE}/check-auth`)
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  /* ---------- load data once authed ---------- */
  useEffect(() => {
    if (!authed) return;
    authFetch(`${API_BASE}/news`)
      .then((r) => r.json())
      .then(toArray)
      .then(setNews)
      .finally(() => setNL(false));
    authFetch(`${API_BASE}/employees`)
      .then((r) => r.json())
      .then(toArray)
      .then(setEmps)
      .finally(() => setEL(false));
    authFetch(`${API_BASE}/recommendations`)
      .then((r) => r.json())
      .then(toArray)
      .then(setRecs)
      .finally(() => setRL(false));
  }, [authed]);

  /* ---------- CROP MODAL GATEWAY ---------- */
  const [cropState, setCropState] = useState({
    open: false,
    mode: "people", // "people" | "newsRect"
    file: null,
    presets: [], // only for people mode
    onDone: null,
  });

  // helper
  const openCrop = (opts, onDone) => {
    setCropState({
      open: true,
      mode: opts.mode,
      file: opts.file,
      presets: opts.presets || [],
      onDone,
    });
  };

  /* ---------- NEWS actions (authFetch) ------- */
  const saveArticle = async () => {
    if (!title || !category || !blocks.length) return alert("Fill all fields");
    await authFetch(`${API_BASE}/news`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, category, published_at: published, blocks }),
    });
    setNews(
      await authFetch(`${API_BASE}/news`).then((r) => r.json()).then(toArray)
    );
    setTitle("");
    setCategory("");
    setPub(todayISO());
    setBlocks([]);
  };
  const deleteNews = async (id) => {
    if (!window.confirm("Delete article?")) return;
    await authFetch(`${API_BASE}/news/${id}`, { method: "DELETE" });
    setNews(
      await authFetch(`${API_BASE}/news`).then((r) => r.json()).then(toArray)
    );
  };

  /* ---------- EMP actions -------------------- */
  const saveEmployee = async () => {
    if (!eName || !eRole || !eBranch || !eFile) return alert("Fill all fields");
    const fd = new FormData();
    fd.append("name", eName);
    fd.append("role", eRole);
    fd.append("branch", eBranch);
    if (eCrop) fd.append("crop_json", JSON.stringify(eCrop));
    fd.append("image", eFile);

    await authFetch(`${API_BASE}/employees`, { method: "POST", body: fd });
    setEmps(
      await authFetch(`${API_BASE}/employees`).then((r) => r.json()).then(toArray)
    );

    // reset fields + file input text
    setEName("");
    setERole("");
    setEBranch("");
    setEFile(null);
    setECrop(null);
    if (eFileRef.current) eFileRef.current.value = null;

    // success flash
    setESavedOK(true);
    setTimeout(() => setESavedOK(false), 1400);
  };

  const deleteEmp = async (id) => {
    if (!window.confirm("Delete employee?")) return;
    await authFetch(`${API_BASE}/employees/${id}`, { method: "DELETE" });
    setEmps(
      await authFetch(`${API_BASE}/employees`).then((r) => r.json()).then(toArray)
    );
  };

  /* ---------- REC actions -------------------- */
  const saveRec = async () => {
    if (!rQuote || !rName || !rFile) return alert("quote, name, image");
    if (rQuote.length > 80) return alert("Quote is too long. Please keep it under 80 characters for optimal display.");
    const fd = new FormData();
    fd.append("quote", rQuote);
    fd.append("name", rName);
    fd.append("title", rTitle);
    fd.append("image", rFile);
    if (rCrop) fd.append("crop_json", JSON.stringify(rCrop));

    await authFetch(`${API_BASE}/recommendations`, { method: "POST", body: fd });
    setRecs(
      await authFetch(`${API_BASE}/recommendations`)
        .then((r) => r.json())
        .then(toArray)
    );

    // reset fields + file input text
    setRQuote("");
    setRName("");
    setRTitle("");
    setRFile(null);
    setRCrop(null);
    if (rFileRef.current) rFileRef.current.value = null;

    // success flash
    setRSavedOK(true);
    setTimeout(() => setRSavedOK(false), 1400);
  };

  const deleteRec = async (id) => {
    if (!window.confirm("Delete recommendation?")) return;
    await authFetch(`${API_BASE}/recommendations/${id}`, { method: "DELETE" });
    setRecs(
      await authFetch(`${API_BASE}/recommendations`)
        .then((r) => r.json())
        .then(toArray)
    );
  };

  /* ---------- logout ------------------------- */
  const logout = async () => {
    await fetch(`${API_BASE}/logout`, { method: "POST", credentials: "include" });
    setAuthed(false);
  };

  /* ========== RENDER ========== */

  if (authed === null) return null; // checking session
  if (authed === false) return <LoginForm onSuccess={() => setAuthed(true)} />;

  // PEOPLE mode presets (Employees & Recs). Portrait 1:1.4 (correct orientation) + circle.
  const peoplePresets = [
    { key: "avatarCircle", label: "Avatar (Circle)", shape: "circle" },
    {
      key: "employeeCard",
      label: "Portrait (1:1.4)",
      shape: "rect",
      aspect: 1 / 1.4,
    },
  ];

  return (
    <div className="news-page-wrapper">
      <Header />

      <main className="news-page">
        <h1>
          Admin <button onClick={logout} style={{ float: "right" }}>Logout</button>
        </h1>

        {/* Tab buttons */}
        <div style={{ marginBottom: "1.5rem" }}>
          {["news", "employees", "recs"].map((t) => (
            <button
              key={t}
              className={`filter-btn ${tab === t ? "active" : ""}`}
              onClick={() => setTab(t)}
            >
              {t === "news" ? "News" : t === "employees" ? "Employees" : "Recs"}
            </button>
          ))}
        </div>

        {/* ------------ NEWS TAB ------------ */}
        {tab === "news" && (
          <>
            <section className="news-card" style={{ marginBottom: "2rem" }}>
              <h3>Add / Edit Article</h3>
              <input
                placeholder="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                style={{ width: "100%", marginBottom: ".5rem" }}
              />
              <input
                placeholder="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{ width: "100%", marginBottom: ".5rem" }}
              />
              <input
                type="date"
                value={published}
                onChange={(e) => setPub(e.target.value)}
                style={{ marginBottom: "1rem" }}
              />
              <BlockEditor
                blocks={blocks}
                setBlocks={setBlocks}
                openCrop={openCrop}
              />
              <button className="primary-save" onClick={saveArticle}>Save article</button>
            </section>

            <h2>Existing articles</h2>
            {nLoad && <p>Loading…</p>}
            {Array.isArray(news) &&
              news.map((n) => (
                <div
                  key={n.news_id}
                  className="news-card"
                  style={{ marginBottom: "1rem" }}
                >
                  <strong>{n.title}</strong> — {n.category}
                  <button
                    style={{ float: "right" }}
                    onClick={() => deleteNews(n.news_id)}
                  >
                    Delete
                  </button>
                </div>
              ))}
          </>
        )}

        {/* ---------- EMPLOYEES TAB ---------- */}
        {tab === "employees" && (
          <>
            <section className={`news-card ${eSavedOK ? "flash-success" : ""}`} style={{ marginBottom: "2rem" }}>
              <h3 style={{ display: "flex", alignItems: "center", gap: 10 }}>
                Add Employee
                {eSavedOK && <span className="save-badge">Saved ✓</span>}
              </h3>
              <input
                placeholder="Name"
                value={eName}
                onChange={(e) => setEName(e.target.value)}
                style={{ width: "100%", marginBottom: ".5rem" }}
              />
              <input
                placeholder="Role"
                value={eRole}
                onChange={(e) => setERole(e.target.value)}
                style={{ width: "100%", marginBottom: ".5rem" }}
              />
              <input
                placeholder="Branch"
                value={eBranch}
                onChange={(e) => setEBranch(e.target.value)}
                style={{ width: "100%", marginBottom: ".5rem" }}
              />
              <input
                ref={eFileRef}
                type="file"
                accept="image/*"
                onClick={(e) => { e.target.value = null; }}
                onChange={(e) => {
                  const f = e.target.files[0] || null;
                  if (!f) return;
                  openCrop({ file: f, mode: "people", presets: peoplePresets }, (cropJson) => {
                    setEFile(f);
                    setECrop(cropJson);
                  });
                }}
                style={{ marginBottom: "1rem" }}
              />
              <button className={`primary-save ${eSavedOK ? "success" : ""}`} onClick={saveEmployee}>
                Save employee
              </button>
            </section>

            <h2>Existing employees</h2>
            {eLoad && <p>Loading…</p>}
            {Array.isArray(emps) &&
              emps.map((e) => {
                const styles = getAvatarCropStyles(e.image_json, "avatarCircle", 80);
                const hasCrop = !!styles;
                return (
                  <div
                    key={e.employee_id}
                    className="news-card"
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div
                      className={`admin-avatar ${hasCrop ? "has-crop" : "no-crop"}`}
                      style={styles?.container || {
                        width: 80, height: 80, borderRadius: "50%", overflow: "hidden",
                        background: "#f3f3f3", marginRight: "1rem"
                      }}
                    >
                      <img
                        src={e.image_path}
                        alt=""
                        className={`admin-avatar__img ${hasCrop ? "has-crop" : "no-crop"}`}
                        style={styles?.img}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <strong>{e.name}</strong> — {e.role} ({e.branch})
                    </div>
                    <button onClick={() => deleteEmp(e.employee_id)}>Delete</button>
                  </div>
                );
              })}
          </>
        )}

        {/* ------ RECOMMENDATIONS TAB ---- */}
        {tab === "recs" && (
          <>
            <section className={`news-card ${rSavedOK ? "flash-success" : ""}`} style={{ marginBottom: "2rem" }}>
              <h3 style={{ display: "flex", alignItems: "center", gap: 10 }}>
                Add Recommendation
                {rSavedOK && <span className="save-badge">Saved ✓</span>}
              </h3>
              <div style={{ marginBottom: ".5rem" }}>
                <textarea
                  rows={3}
                  placeholder="Quote (max 80 characters)"
                  value={rQuote}
                  onChange={(e) => setRQuote(e.target.value)}
                  style={{
                    width: "100%",
                    border: rQuote.length > 80 ? "2px solid #ff6b6b" : "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "8px"
                  }}
                  maxLength={80}
                />
                <div style={{
                  fontSize: "0.8rem",
                  color: rQuote.length > 80 ? "#ff6b6b" : "#666",
                  textAlign: "right",
                  marginTop: "4px"
                }}>
                  {rQuote.length}/80 characters
                </div>
              </div>
              <input
                placeholder="Name"
                value={rName}
                onChange={(e) => setRName(e.target.value)}
                style={{ width: "100%", marginBottom: ".5rem" }}
              />
              <input
                placeholder="Title (optional)"
                value={rTitle}
                onChange={(e) => setRTitle(e.target.value)}
                style={{ width: "100%", marginBottom: ".5rem" }}
              />
              <input
                ref={rFileRef}
                type="file"
                accept="image/*"
                onClick={(e) => { e.target.value = null; }}
                onChange={(e) => {
                  const f = e.target.files[0] || null;
                  if (!f) return;
                  openCrop({ file: f, mode: "people", presets: peoplePresets }, (cropJson) => {
                    setRFile(f);
                    setRCrop(cropJson);
                  });
                }}
                style={{ marginBottom: "1rem" }}
              />

              <button className={`primary-save ${rSavedOK ? "success" : ""}`} onClick={saveRec}>
                Save recommendation
              </button>
            </section>

            <h2>Existing recommendations</h2>
            {rLoad && <p>Loading…</p>}
            {Array.isArray(recs) &&
              recs.map((r) => {
                const styles = getAvatarCropStyles(r.image_json, "avatarCircle", 50);
                const hasCrop = !!styles;
                return (
                  <div
                    key={r.rec_id}
                    className="news-card"
                    style={{
                      marginBottom: "1rem",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div
                      className={`admin-avatar ${hasCrop ? "has-crop" : "no-crop"}`}
                      style={styles?.container || {
                        width: 50, height: 50, borderRadius: "50%", overflow: "hidden",
                        background: "#f3f3f3", marginRight: "1rem"
                      }}
                    >
                      <img
                        src={r.image_path}
                        alt=""
                        className={`admin-avatar__img ${hasCrop ? "has-crop" : "no-crop"}`}
                        style={styles?.img}
                      />
                    </div>
                    <div style={{ flex: 1, textAlign: "left" }}>
                      <em>"{r.quote_text.slice(0, 80)}…"</em>
                      <br />— {r.name}{r.title ? `, ${r.title}` : ""}
                    </div>
                    <button onClick={() => deleteRec(r.rec_id)}>Delete</button>
                  </div>
                );
              })}
          </>
        )}
      </main>

      <Footer />

      {/* Global Image Crop Modal */}
      {cropState.open && (
        <ImageCropModal
          mode={cropState.mode} // "people" or "newsRect"
          file={cropState.file}
          presets={cropState.presets} // only used for "people"
          onCancel={() => setCropState((s) => ({ ...s, open: false }))}
          onConfirm={(cropJson) => {
            cropState.onDone?.(cropJson);
            setCropState((s) => ({ ...s, open: false }));
          }}
        />
      )}
    </div>
  );
};

export default Admin;
